/**
 * The Notes tool (#157) — place a written note on the canvas.
 *
 * Two gestures, told apart by where the press LANDED rather than by a mode
 * switch:
 *
 *  - **Press on a part and drag.** The note lands where the pointer is
 *    released and keeps a leader line back to the exact point pressed, so
 *    "this corner, not that one" is expressible in one motion.
 *  - **Press on empty canvas.** A free note appears there with no leader —
 *    a general remark that belongs to the pattern rather than to a part.
 *
 * A drag that starts on a part but travels almost nowhere still produces an
 * attached note; the leader is just short. Requiring a minimum travel would
 * make the gesture fail silently for someone who meant to tap.
 */
import type { PointerSample } from "../canvas/PointerInput";
import type { EditorContext } from "./EditorContext";
import { ToolBase } from "./ToolBase";
import type { Note, Project } from "../model/Project";
import {
  DEFAULT_NOTE_FONT_MM,
  DEFAULT_NOTE_HEIGHT_MM,
  DEFAULT_NOTE_WIDTH_MM,
  noteAnchorFor,
  type NoteTarget,
} from "../model/notes";
import { cssVar } from "../colors";
// The SAME radius the committed tip dot uses. Previewing at the badge size
// made the dot jump smaller the instant the drag ended.
import { ANCHOR_DOT_RADIUS_MM } from "../canvas/noteRenderer";
import { focusNoteTextOnNextRender } from "../ui/notePanelSection";
import { NoteDragController } from "./noteDrag";

export class NoteTool extends ToolBase {
  id = "notes";
  label = "Notes";
  cursor = "crosshair";
  shortcut = "n";

  /** Where the press landed, and on what — captured on down so the drag can be
   *  classified on release. */
  private pressAt: { x: number; y: number } | null = null;
  /** What the press landed on — the part, refined to its cutout or seam when
   *  the press was on one (#1076). Null off the leather. */
  private pressTarget: NoteTarget | null = null;
  /**
   * Dragging a note that already EXISTS.
   *
   * Pressing on a note takes precedence over making a new one — otherwise the
   * only way to reposition a note would be to delete it and place another, and
   * every attempt to grab one would litter the canvas with new cards. Shared
   * with the Select tool, which offers the same grab without leaving whatever
   * mode the maker is in.
   */
  private readonly noteDrag = new NoteDragController(this.ctx);
  /** Ephemeral leader (and its tip dot) drawn while a new note is being
   *  dragged out of a part, so the maker can see what the callout will point
   *  at, and where its tip will land, before letting go. */
  private previewLine: paper.Path | null = null;
  private previewDot: paper.Path | null = null;

  constructor(ctx: EditorContext) {
    super(ctx);
  }

  onDown(_s: PointerSample, p: { x: number; y: number }): void {
    this.pressAt = { x: p.x, y: p.y };
    const scope = this.ctx.scene.getPaperScope();

    // An existing note under the cursor wins over placing a new one.
    if (this.noteDrag.begin(p)) return;

    const hit = this.ctx.scene.hitTest(new scope.Point(p.x, p.y));
    this.pressTarget = hit.pieceId
      ? {
          pieceId: hit.pieceId,
          ...(hit.cutoutId
            ? { cutoutId: hit.cutoutId }
            : hit.stitchId
              ? { stitchId: hit.stitchId }
              : {}),
        }
      : null;
  }

  /** Hovering an existing note says it can be picked up — a crosshair there
   *  reads as "click to place another", which is the opposite of what a press
   *  will do. */
  onHover(_s: PointerSample, p: { x: number; y: number }): void {
    const canvas = this.ctx.scene.getPaperScope().view.element as
      | HTMLCanvasElement
      | undefined;
    if (!canvas) return;
    canvas.style.cursor =
      this.noteDrag.cursorOver(p) ?? this.cursor ?? "crosshair";
  }

  onDeactivate(): void {
    const canvas = this.ctx.scene.getPaperScope().view.element as
      | HTMLCanvasElement
      | undefined;
    if (canvas) canvas.style.cursor = "";
    this.clearPreviewLeader();
  }

  onMove(_s: PointerSample, p: { x: number; y: number }): void {
    if (this.noteDrag.move(p)) return;

    // Dragging a new note out of a part: show the leader it will get.
    if (this.pressTarget && this.pressAt)
      this.drawPreviewLeader(this.pressAt, p);
  }

  /** Draw / update the ephemeral leader for a note being dragged out. */
  private drawPreviewLeader(
    from: { x: number; y: number },
    to: { x: number; y: number },
  ): void {
    const scope = this.ctx.scene.getPaperScope();
    if (!this.previewLine || !this.previewLine.parent) {
      const line = new scope.Path.Line({
        from: [from.x, from.y],
        to: [to.x, to.y],
        strokeColor: cssVar("--note-callout"),
        strokeWidth: 0.4,
        strokeScaling: false,
        dashArray: [2, 2],
      });
      // Ephemeral so paper's hit test skips it — it is feedback, never a
      // target, and it must not swallow the release.
      line.data = { kind: "note-callout-preview", ephemeral: true };
      this.previewLine = line;
      // The tip dot, drawn exactly as the committed note will draw it, so the
      // maker is looking at the finished mark rather than guessing where it
      // lands from the end of a dashed line.
      const dot = new scope.Path.Circle({
        center: [from.x, from.y],
        radius: ANCHOR_DOT_RADIUS_MM,
        fillColor: cssVar("--note-callout"),
      });
      dot.data = { kind: "note-anchor-preview", ephemeral: true };
      this.previewDot = dot;
      return;
    }
    this.previewLine.segments[0]!.point = new scope.Point(from.x, from.y);
    this.previewLine.segments[1]!.point = new scope.Point(to.x, to.y);
    if (this.previewDot) {
      this.previewDot.position = new scope.Point(from.x, from.y);
    }
  }

  private clearPreviewLeader(): void {
    this.previewLine?.remove();
    this.previewLine = null;
    this.previewDot?.remove();
    this.previewDot = null;
  }

  onUp(_s: PointerSample, p: { x: number; y: number }): void {
    this.clearPreviewLeader();
    if (this.noteDrag.end(p)) {
      this.pressAt = null;
      this.pressTarget = null;
      return;
    }

    const press = this.pressAt;
    const target = this.pressTarget;
    this.pressAt = null;
    this.pressTarget = null;
    if (!press) return;

    const scope = this.ctx.scene.getPaperScope();
    const id = `note-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 7)}`;

    this.ctx.commit("Add note", (project: Project) => {
      const piece = target
        ? (project.pieces.find((pc) => pc.id === target.pieceId) ?? null)
        : null;
      // The anchor is measured from the PRESS, not the release — the press is
      // the thing the maker aimed at; the release only says where the card goes.
      const anchor =
        piece && target ? noteAnchorFor(piece, press, scope, target) : null;

      // Place the card so the leader ENDS exactly where the drag ended.
      //
      // The leader meets the card at the anchor clamped into the card's
      // rectangle, so putting the corner nearest the anchor on the release
      // point makes that clamp land on the release point in both axes — the
      // line stops where the cursor stopped instead of jumping to an edge
      // when the pointer is let go. It also means the card hangs AWAY from
      // the part rather than always down-right, so it never covers what was
      // just pointed at.
      const position = anchor
        ? {
            x: p.x >= press.x ? p.x : p.x - DEFAULT_NOTE_WIDTH_MM,
            y: p.y >= press.y ? p.y : p.y - DEFAULT_NOTE_HEIGHT_MM,
          }
        : { x: p.x, y: p.y };

      const note: Note = {
        id,
        // Empty, not pre-filled — see NOTE_PLACEHOLDER.
        text: "",
        position,
        widthMm: DEFAULT_NOTE_WIDTH_MM,
        heightMm: DEFAULT_NOTE_HEIGHT_MM,
        fontSizeMm: DEFAULT_NOTE_FONT_MM,
        ...(anchor ? { anchors: [anchor] } : {}),
      };
      return { ...project, notes: [...(project.notes ?? []), note] };
    });

    // Armed BEFORE the selection, because selecting is what refreshes the
    // panel and renders the section that consumes this. Placing a note is a
    // statement that the maker has something to say, so the cursor should
    // already be in the box rather than one more click away.
    focusNoteTextOnNextRender(id);
    this.ctx.setSelection({
      pieceId: null,
      decorationId: null,
      noteIds: [id],
    });
  }

  /**
   * Escape backs out of whatever is in flight (#157): a note being dragged out
   * of a part is abandoned rather than placed, and a note being moved or
   * re-pointed snaps back to where it was.
   *
   * Returns false when nothing is in flight, so the app's Escape chain falls
   * through to whatever else Escape means.
   */
  handleEscape(): boolean {
    const busy =
      this.pressAt !== null || this.noteDrag.busy || this.previewLine !== null;
    if (!busy) return false;
    this.onCancel();
    return true;
  }

  onCancel(): void {
    this.clearPreviewLeader();
    this.noteDrag.cancel();
    this.pressAt = null;
    this.pressTarget = null;
    this.ctx.rerender();
  }
}
